/**
 * Utility functions for formatting various data types
 */

/**
 * Format currency from cents to euros
 * @param cents Amount in cents
 * @returns Formatted currency string (e.g., "€10.50")
 */
export const formatCurrency = (cents: number): string => {
  return (cents / 100).toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR'
  });
};

/**
 * Format duration from seconds to hours and minutes
 * @param seconds Duration in seconds
 * @returns Formatted duration string (e.g., "2h 30m")
 */
export const formatDuration = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${minutes}m`;
};

/**
 * Format distance from meters to kilometers
 * @param meters Distance in meters
 * @returns Formatted distance string (e.g., "10.5 km")
 */
export const formatDistance = (meters: number): string => {
  return (meters / 1000).toLocaleString('de-DE', {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1
  }) + ' km';
};

/**
 * Format date object to a readable string
 * @param dateObj Date object or string
 * @param includeTime
 * @returns Formatted date string or "-" if invalid
 */
export const formatDate = (dateObj: any, includeTime = true): string => {
  if (!dateObj) return '-';
  try {
    if (dateObj.date) {
      return new Date(dateObj.date).toLocaleDateString('de-DE', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        ...(includeTime ? { hour: '2-digit', minute: '2-digit', second: '2-digit' } : {})
      });
    }
    return new Date(dateObj).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      ...(includeTime ? { hour: '2-digit', minute: '2-digit', second: '2-digit' } : {})
    });
  } catch (err) {
    return dateObj.date || dateObj || '-';
  }
};

/**
 * Format ISO date string to a readable date and time
 * @param dateString ISO date string
 * @returns Formatted date and time string or "-" if invalid
 */
export const formatDateTime = (dateString: string): string => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (err) {
    return dateString || '-';
  }
};
